<script setup>
import { h, computed, onMounted, ref, watch, nextTick } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import SidebarGroup from './SidebarGroup.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import Icon from 'next/icon/Icon.vue';
import TrialIndicator from './TrialIndicator.vue';
import { buildCrmMenu } from './helper/buildCrmMenu';
import { buildMarketingMenu } from './helper/buildMarketingMenu';
import marketingAPI from 'dashboard/api/marketing';
import { buildTasksMenu } from './helper/buildTasksMenu';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'closeMobileSidebar',
]);

// Prevent Vue warnings about unhandled event listeners in fragment components
defineOptions({
  inheritAttrs: false,
});

const { accountScopedRoute, currentAccount } = useAccount();

const store = useStore();
const { t } = useI18n();

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// Sidebar collapsed/expanded state - persisted in localStorage
const isSidebarCollapsed = useStorage('sidebar-collapsed', true, localStorage);

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value;
};

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

// Estado para o menu ativo (que mostra o painel secundário)
const activeMenuItem = ref(null);

const setActiveMenuItem = menuItem => {
  if (activeMenuItem.value?.name === menuItem?.name) {
    activeMenuItem.value = null;
  } else {
    activeMenuItem.value = menuItem;
  }
};

const closeSecondaryPanel = () => {
  activeMenuItem.value = null;
};

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  activeMenuItem,
  setActiveMenuItem,
});

const globalConfig = useMapGetter('globalConfig/get');
const appVersion = computed(() => globalConfig.value?.appVersion || '');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const dashboardAppsOnSidebar = useMapGetter('dashboardApps/getAppsOnSidebar');

// Agent restriction features removed - always show all sections
const hideAllInboxForAgent = computed(() => false);
const hideContactsForAgent = computed(() => false);

// Status das conexões de anúncio: os itens Meta Ads/Google Ads/Captura só
// aparecem no menu quando a conta está CONECTADA (decisão Ivan 26/08) —
// quem ainda não conectou usa a tela Conexões. Falha na chamada = itens
// ocultos (nunca menu fantasma).
const adsStatus = ref({ meta: false, google: false });
async function fetchAdsStatus() {
  try {
    const { data } = await marketingAPI.connections();
    adsStatus.value = {
      meta: data.meta_ads?.ads_connected || false,
      google: data.google_ads?.connected || false,
    };
  } catch {
    adsStatus.value = { meta: false, google: false };
  }
}

onMounted(() => {
  fetchAdsStatus();
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('funnels/get');
  store.dispatch('dashboardApps/get');
  store.dispatch('chatRooms/get');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const openNewConversationModal = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const closeNewConversationModal = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

// Quick Actions Popover
const isQuickActionsOpen = ref(false);
const quickActionsRef = ref(null);
const quickActionsPopoverRef = ref(null);

// New Conversation Modal
const newConversationTriggerRef = ref(null);

const toggleQuickActions = () => {
  isQuickActionsOpen.value = !isQuickActionsOpen.value;
};

const closeQuickActions = () => {
  isQuickActionsOpen.value = false;
};

const handleNewConversation = () => {
  closeQuickActions();
  openNewConversationModal();
  nextTick(() => {
    newConversationTriggerRef.value?.click();
  });
};

// "+" colapsado abre o popover (busca + nova conversa); expandido, como já há
// um botão de busca ao lado, vai direto para nova conversa.
const handlePlusClick = () => {
  if (isSidebarCollapsed.value) {
    toggleQuickActions();
  } else {
    handleNewConversation();
  }
};

// Click outside to close quick actions
const handleQuickActionsClickOutside = event => {
  const isInsidePopover =
    quickActionsPopoverRef.value &&
    quickActionsPopoverRef.value.contains(event.target);
  const isInsideTrigger =
    quickActionsRef.value && quickActionsRef.value.contains(event.target);

  if (!isInsidePopover && !isInsideTrigger) {
    closeQuickActions();
  }
};

watch(isQuickActionsOpen, newVal => {
  if (newVal) {
    setTimeout(() => {
      document.addEventListener('click', handleQuickActionsClickOutside, true);
    }, 50);
  } else {
    document.removeEventListener('click', handleQuickActionsClickOutside, true);
  }
});

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    icon: 'i-lucide-user',
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    icon: 'i-lucide-bookmark',
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    icon: 'i-lucide-mail',
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    icon: 'i-lucide-users-round',
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  return [
    {
      name: 'Dashboard',
      label: t('SIDEBAR.DASHBOARD'),
      icon: 'i-lucide-layout-dashboard',
      to: accountScopedRoute('main_dashboard'),
      activeOn: ['main_dashboard'],
    },
    // DISABLED: inbox-view feature
    // {
    //   name: 'Inbox',
    //   label: t('SIDEBAR.INBOX'),
    //   icon: 'i-lucide-inbox',
    //   to: accountScopedRoute('inbox_view'),
    //   activeOn: ['inbox_view', 'inbox_view_conversation'],
    //   getterKeys: {
    //     count: 'notifications/getUnreadCount',
    //   },
    // },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-messages-square',
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-lucide-at-sign',
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          icon: 'i-lucide-clock',
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        // Only show Channels section if feature flag is not enabled
        ...(!hideAllInboxForAgent.value
          ? [
              {
                name: 'Channels',
                label: t('SIDEBAR.CHANNELS'),
                icon: 'i-lucide-mailbox',
                activeOn: ['conversation_through_inbox'],
                children: sortedInboxes.value.map(inbox => ({
                  name: `${inbox.name}-${inbox.id}`,
                  label: inbox.name,
                  to: accountScopedRoute('inbox_dashboard', {
                    inbox_id: inbox.id,
                  }),
                  component: leafProps =>
                    h(ChannelLeaf, {
                      label: leafProps.label,
                      active: leafProps.active,
                      inbox,
                    }),
                })),
              },
            ]
          : []),
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // Chat Suite (AI Studio) mudou pra DENTRO do Workspace (decisão Ivan
    // 27/08) — ver buildTasksMenu.
    buildCrmMenu({ t, accountScopedRoute }),
    // Tarefas — ferramenta interna de quadros (Trello/Monday), isolada do funil.
    buildTasksMenu({ t, accountScopedRoute }),
    // AI Agents (Agno nativo) — herdou o ícone sparkles do antigo "Agentes LIA"
    // (removido do menu; rota lia_agents_* segue acessível por URL direta).
    {
      name: 'AiAgents',
      icon: 'i-lucide-sparkles',
      label: t('SIDEBAR.AI_AGENTS'),
      activeOn: [
        'ai_agents_list',
        'ai_agents_create',
        'ai_agents_settings',
        'ai_agents_inboxes',
        'ai_agents_rules',
        'ai_connectors_list',
      ],
      to: accountScopedRoute('ai_agents_list'),
    },
    {
      name: 'ChatRooms',
      label: t('CHAT_ROOMS.SIDEBAR.TITLE'),
      icon: 'i-lucide-message-square-plus',
      to: accountScopedRoute('chat_rooms_index'),
      activeOn: ['chat_rooms_index', 'chat_rooms'],
      featureFlag: 'chat_rooms',
      getterKeys: {
        count: 'chatRooms/getTotalUnreadCount',
      },
    },
    // Only show Contacts section if feature flag is not enabled
    ...(!hideContactsForAgent.value
      ? [
          {
            name: 'Contacts',
            label: t('SIDEBAR.CONTACTS'),
            icon: 'i-lucide-contact',
            children: [
              {
                name: 'All Contacts',
                label: t('SIDEBAR.ALL_CONTACTS'),
                icon: 'i-lucide-contact-round',
                to: accountScopedRoute(
                  'contacts_dashboard_index',
                  {},
                  { page: 1, search: undefined }
                ),
                activeOn: ['contacts_dashboard_index', 'contacts_edit'],
              },
              {
                name: 'Import Contacts',
                label: t('SIDEBAR.IMPORT_CONTACTS'),
                icon: 'i-lucide-upload',
                to: accountScopedRoute('contact_import'),
                activeOn: ['contact_import'],
              },
              {
                name: 'Active',
                label: t('SIDEBAR.ACTIVE'),
                icon: 'i-lucide-user-check',
                to: accountScopedRoute('contacts_dashboard_active'),
                activeOn: ['contacts_dashboard_active'],
              },
              {
                name: 'Segments',
                icon: 'i-lucide-layers',
                label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
                children: contactCustomViews.value.map(view => ({
                  name: `${view.name}-${view.id}`,
                  label: view.name,
                  to: accountScopedRoute(
                    'contacts_dashboard_segments_index',
                    { segmentId: view.id },
                    { page: 1 }
                  ),
                  activeOn: [
                    'contacts_dashboard_segments_index',
                    'contacts_edit_segment',
                  ],
                })),
              },
              {
                name: 'Tagged With',
                icon: 'i-lucide-hash',
                label: t('SIDEBAR.TAGGED_WITH'),
                children: labels.value.map(label => ({
                  name: `${label.title}-${label.id}`,
                  label: label.title,
                  icon: h('span', {
                    class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
                    style: { backgroundColor: label.color },
                  }),
                  to: accountScopedRoute(
                    'contacts_dashboard_labels_index',
                    { label: label.title },
                    { page: 1, search: undefined }
                  ),
                  activeOn: [
                    'contacts_dashboard_labels_index',
                    'contacts_edit_label',
                  ],
                })),
              },
            ],
          },
        ]
      : []),
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          icon: 'i-lucide-pie-chart',
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          icon: 'i-lucide-message-square-text',
          to: accountScopedRoute('conversation_reports'),
        },
        {
          name: 'Operational Reports',
          label: t('SIDEBAR.OPERATIONAL_REPORT'),
          icon: 'i-lucide-chart-no-axes-combined',
          to: accountScopedRoute('operational_reports'),
          activeOn: ['operational_reports'],
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          icon: 'i-lucide-smile',
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          icon: 'i-lucide-timer',
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          icon: 'i-lucide-bot-message-square',
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    // Marketing (decisão Ivan 25/08/2026): hub único de aquisição — Estúdio
    // Social, Anúncios, Campanhas, Automação de comentários, Prospecção e
    // Links & QR num menu só. Rotas e gates intactos; só a organização mudou
    // (Campanhas e Estúdio Social deixaram de ser menus próprios, e os itens
    // de captação saíram de CRM → Captação).
    buildMarketingMenu({ t, accountScopedRoute, ads: adsStatus.value }),
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        // Academia ChatSuite: treinamentos da PLATAFORMA, somente leitura,
        // visível pra qualquer conta (conteúdo vem do portal training_platform).
        {
          name: 'Academy',
          label: t('SIDEBAR.HELP_CENTER.TRAINING'),
          icon: 'i-lucide-graduation-cap',
          activeOn: ['academy_index', 'academy_lesson'],
          to: accountScopedRoute('academy_index'),
        },
        // Wiki da plataforma: mesmo catálogo, filtrado no acervo "wiki".
        {
          name: 'PlatformWiki',
          label: t('SIDEBAR.HELP_CENTER.WIKI'),
          icon: 'i-lucide-book-open',
          activeOn: ['academy_wiki'],
          to: accountScopedRoute('academy_wiki'),
        },
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          icon: 'i-lucide-file-text',
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          icon: 'i-lucide-folder-tree',
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          icon: 'i-lucide-globe',
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          icon: 'i-lucide-cog',
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    // Dashboard Apps (Custom iframes) - only shown if there are apps with show_on_sidebar enabled
    ...(dashboardAppsOnSidebar.value.length > 0
      ? [
          {
            name: 'Dashboard Apps',
            label: t('SIDEBAR.DASHBOARD_APPS'),
            icon: 'i-lucide-app-window',
            children: dashboardAppsOnSidebar.value.map(app => ({
              name: `${app.title}-${app.id}`,
              label: app.title,
              to: accountScopedRoute('dashboard_app_view', { id: app.id }),
              activeOn: ['dashboard_app_view'],
            })),
          },
        ]
      : []),
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-building',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-network',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          to: accountScopedRoute('assignment_policy_index'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-zap',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-scroll-text',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Call Devices',
          label: t('SIDEBAR.CALL_DEVICES'),
          icon: 'i-lucide-phone',
          to: accountScopedRoute('call_devices_list'),
        },
      ],
    },
    {
      name: 'Subscription',
      label: t('SIDEBAR.SUBSCRIPTION'),
      icon: 'i-lucide-credit-card',
      to: accountScopedRoute('billing_settings_index'),
      activeOn: ['billing_settings_index', 'billing_pricing'],
    },
    // Reseller panel — only for partner (reseller) accounts.
    // Non-partner accounts see a "Seja parceiro" entry to apply (self-service).
    ...(currentAccount.value?.reseller
      ? [
          {
            name: 'revenda',
            label: t('SIDEBAR.REVENDA'),
            icon: 'i-lucide-store',
            to: accountScopedRoute('revenda_index'),
            activeOn: ['revenda_index'],
          },
        ]
      : [
          {
            name: 'seja_revenda',
            label: t('SIDEBAR.SEJA_REVENDA'),
            icon: 'i-lucide-handshake',
            to: accountScopedRoute('seja_revenda'),
            activeOn: ['seja_revenda'],
          },
        ]),
    {
      name: 'Changelog',
      label: t('SIDEBAR.CHANGELOG'),
      icon: 'i-lucide-megaphone',
      to: accountScopedRoute('changelog'),
      activeOn: ['changelog'],
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 md:static md:flex-shrink-0 transition-[width] duration-200"
    :class="[
      isSidebarCollapsed ? 'w-[60px]' : 'w-[220px]',
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full md:ltr:translate-x-0 md:rtl:-translate-x-0':
          !isMobileSidebarOpen,
      },
    ]"
  >
    <section class="grid gap-2 mt-3 mb-3">
      <div
        class="flex items-center gap-1 px-2"
        :class="isSidebarCollapsed ? 'flex-col' : 'justify-start'"
      >
        <!-- Busca: só no modo expandido; no colapsado a busca fica no popover
             do "+". Navega para a mesma tela de busca do popover. -->
        <RouterLink
          v-if="!isSidebarCollapsed"
          :to="{ name: 'search' }"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          class="flex items-center justify-center rounded-lg size-9 shrink-0 hover:bg-n-alpha-2 transition-colors text-n-slate-11"
        >
          <span class="flex-shrink-0 i-lucide-search size-4" />
        </RouterLink>

        <div class="relative flex justify-center shrink-0">
          <button
            ref="quickActionsRef"
            :title="
              isSidebarCollapsed
                ? t('SIDEBAR.QUICK_ACTIONS')
                : t('SIDEBAR.NEW_CONVERSATION')
            "
            class="flex items-center justify-center rounded-lg size-9 bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
            :class="{ 'bg-n-alpha-2': isQuickActionsOpen }"
            @click="handlePlusClick"
          >
            <span class="flex-shrink-0 i-lucide-plus size-4 text-n-slate-11" />
          </button>
          <!-- Quick Actions Popover -->
          <div
            v-if="isQuickActionsOpen"
            ref="quickActionsPopoverRef"
            class="fixed z-[100] min-w-[180px] bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl shadow-lg py-2 px-2"
            :class="isSidebarCollapsed ? 'left-[68px]' : 'left-[228px]'"
            :style="{
              top: quickActionsRef?.getBoundingClientRect?.()?.top + 'px',
            }"
          >
            <RouterLink
              :to="{ name: 'search' }"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="closeQuickActions"
            >
              <span class="i-lucide-search size-4 text-n-slate-11" />
              <span>{{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}</span>
            </RouterLink>
            <button
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors w-full text-left"
              @click="handleNewConversation"
            >
              <span class="i-lucide-pencil size-4 text-n-slate-11" />
              <span>{{ t('SIDEBAR.NEW_CONVERSATION') }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
    <nav class="grid flex-grow gap-2 px-2 pb-5 overflow-y-scroll no-scrollbar">
      <ul
        class="flex flex-col gap-1.5 m-0 list-none"
        :class="isSidebarCollapsed ? 'items-center' : 'items-stretch'"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
          :is-collapsed="isSidebarCollapsed"
          :active-menu-name="activeMenuItem?.name"
          @open-submenu="setActiveMenuItem"
          @close-submenu="closeSecondaryPanel"
        />
      </ul>
    </nav>
    <TrialIndicator :is-collapsed="isSidebarCollapsed" class="shrink-0" />
    <!-- Indicador de storage saiu daqui (decisão Ivan 26/08): mora só
         dentro do Drive (Workspace), que tem a barra + "comprar mais". -->
    <section
      class="flex items-center gap-2 px-2 py-2 border-t border-n-weak shrink-0"
      :class="
        isSidebarCollapsed ? 'flex-col justify-center' : 'justify-between'
      "
    >
      <span
        v-if="!isSidebarCollapsed && appVersion"
        class="text-xs truncate text-n-slate-10"
        :title="`Versão ${appVersion}`"
      >
        {{ `Versão ${appVersion}` }}
      </span>
      <button
        :title="
          isSidebarCollapsed ? t('SIDEBAR.EXPAND') : t('SIDEBAR.COLLAPSE')
        "
        class="flex items-center justify-center rounded-lg size-9 shrink-0 hover:bg-n-alpha-2 transition-colors text-n-slate-11"
        @click="toggleSidebar"
      >
        <Icon
          :icon="
            isSidebarCollapsed
              ? 'i-lucide-chevrons-right'
              : 'i-lucide-chevrons-left'
          "
          class="size-4"
        />
      </button>
    </section>
  </aside>
  <!-- New Conversation Modal (outside aside to avoid width constraints) -->
  <ComposeConversation is-modal @close="closeNewConversationModal">
    <template #trigger="{ toggle }">
      <span ref="newConversationTriggerRef" class="hidden" @click="toggle" />
    </template>
  </ComposeConversation>
</template>
